import { inject, Injectable } from '@angular/core';
import { IndexedDbService } from '../../indexed-db.service';
import { PhotoQueueService } from '../photo-queue.service';

const LEGACY_SUBMISSION_QUEUE_KEY = 'workflow_submission_queue';
const LEGACY_COMPLETION_QUEUE_KEY = 'workflow_completion_queue';
const LEGACY_CACHE_KEY_PREFIXES = [
  'workflow_data_',
  'workflow_draft_',
  'workflow-errors:',
];

/**
 * Разовая очистка записей регламентов, созданных до изоляции по владельцу (OutkeepDB).
 *
 * Старые глобальные очереди и задачи фото не содержат автора, а по сохранённому
 * на устройстве пользователю его установить нельзя (A мог выйти, B — войти до обновления).
 * Отправка чужих задач под другой сессией недопустима, поэтому такие задачи
 * не переносятся, а удаляются с записью в лог. Старый кэш, черновики и ошибки тоже удаляются.
 * Операция идемпотентна: повторный запуск после частичного сбоя ничего не дублирует.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowStorageMigrationService {
  private readonly indexedDbService = inject(IndexedDbService);
  private readonly photoQueueService = inject(PhotoQueueService);

  async migrateLegacyStorage(): Promise<void> {
    const keys = await this.indexedDbService.getAllKeys();
    const legacyQueueSize = await this.countLegacyQueueItems(keys);

    const legacyKeys = keys.filter(
      key =>
        key === LEGACY_SUBMISSION_QUEUE_KEY ||
        key === LEGACY_COMPLETION_QUEUE_KEY ||
        LEGACY_CACHE_KEY_PREFIXES.some(prefix => key.startsWith(prefix)),
    );

    for (const key of legacyKeys) {
      await this.indexedDbService.removeItem(key);
    }

    const removedPhotoTasks = await this.photoQueueService.removeUnownedTasks();

    if (legacyQueueSize > 0 || removedPhotoTasks > 0) {
      console.warn(
        `[WorkflowStorageMigration] Removed ${legacyQueueSize} queued workflow task(s) and ${removedPhotoTasks} photo task(s) without a known owner`,
      );
    }
  }

  private async countLegacyQueueItems(keys: string[]): Promise<number> {
    let count = 0;

    for (const key of [
      LEGACY_SUBMISSION_QUEUE_KEY,
      LEGACY_COMPLETION_QUEUE_KEY,
    ]) {
      if (keys.includes(key)) {
        const items = await this.indexedDbService.getItem<unknown>(key);

        count += Array.isArray(items) ? items.length : 0;
      }
    }

    return count;
  }
}
